import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, ArrowLeft, LogIn } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../shared/useNotifications';
import ForgotPasswordModal from './ForgotPasswordModal';
import './AuthShared.css';
import logger from '../../utils/logger';

const JudgeLogin = ({ onBackToHome, onGoToAccessRequest, forcedError, onErrorDisplayed }) => {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const { success: showSuccessNotification, error: showErrorNotification } = useNotifications();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);

  // Mostrar error forzado si viene del LoginView
  useEffect(() => {
    if (forcedError) {
      setError(forcedError);
    }
  }, [forcedError]);

  // Resetear error cuando el usuario empieza a escribir
  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    if (error) {
      setError('');
      if (onErrorDisplayed) onErrorDisplayed();
    }
  };
  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    if (error) {
      setError('');
      if (onErrorDisplayed) onErrorDisplayed();
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!email.trim()) {
      setError('Por favor ingresa tu correo');
      setLoading(false);
      return;
    }

    if (!password) {
      setError('Por favor ingresa tu contraseña');
      setLoading(false);
      return;
    }

    try {
      const { data, error: authError } = await signIn(email, password);

      if (authError) {
        setError(authError);
        setLoading(false);
        return;
      }

      // La navegación se maneja desde JudgeLoginView via useEffect
    } catch (err) {
      logger.error('Error en login:', err?.message);
      setError('Error al intentar iniciar sesión. Intenta nuevamente.');
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestAccess = () => {
    onGoToAccessRequest();
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <button
          type="button"
          onClick={onBackToHome}
          className="auth-back"
          aria-label="Volver atrás"
        >
          <ArrowLeft size={18} /> Volver
        </button>

        <div className="auth-header">
          <img src="/letracopagimnasta.png" alt="CopaGimnasta" className="auth-logo" onClick={() => navigate('/')} />
          <h2>Ingreso de Jueces</h2>
          <p>Inicia sesión con tus credenciales</p>
        </div>

        {error && (
          <div className="auth-alert auth-alert-error">
            <AlertCircle size={18} />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleLogin} className="auth-form">
          <div className="auth-form-group">
            <label htmlFor="email">Correo Electrónico</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={handleEmailChange}
              placeholder="tu@correo.com"
              required
              disabled={loading}
            />
          </div>

          <div className="auth-form-group">
            <label htmlFor="password">Contraseña</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={handlePasswordChange}
              placeholder="Tu contraseña"
              required
              disabled={loading}
            />
          </div>

          <button
            type="submit"
            className="auth-btn-primary"
            disabled={loading}
          >
            {loading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
          </button>
        </form>

        <button
          type="button"
          onClick={() => setShowForgot(true)}
          style={{ display: 'block', margin: '12px auto 0', background: 'none', border: 'none', color: '#6b7280', fontSize: '0.82rem', cursor: 'pointer', textDecoration: 'underline' }}
        >
          ¿Olvidaste tu contraseña?
        </button>

        <div className="auth-divider">
          <span>¿No tienes cuenta?</span>
        </div>

        <button
          type="button"
          onClick={handleRequestAccess}
          className="auth-btn-outline"
          disabled={loading}
        >
          Solicitar Acceso
        </button>

        <p className="auth-copyright">
          © {new Date().getFullYear()} CopaGimnasta. Todos los derechos reservados.
        </p>
      </div>
      <ForgotPasswordModal isOpen={showForgot} onClose={() => setShowForgot(false)} />
    </div>
  );
};

export default JudgeLogin;
